import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { useApolloClient, useMutation } from '@apollo/client'
import {
  faCircleExclamation,
  faTruckArrowRight,
} from '@fortawesome/pro-solid-svg-icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { isNil } from 'lodash-es'
import { observer } from 'mobx-react'

import { Alert } from 'Components/_theme/Alert/Alert'
import { ButtonGroup } from 'Components/_theme/ButtonGroup/ButtonGroup'
import { Card } from 'Components/_theme/Card/Card'
import { CardBody } from 'Components/_theme/CardBody/CardBody'
import { CardHeader } from 'Components/_theme/CardHeader/CardHeader'
import { Heading } from 'Components/_theme/Heading/Heading'
import { AlertWithError } from 'Components/AlertWithError/AlertWithError'
import { CountrySelect } from 'Components/CountrySelect/CountrySelect'
import { Field } from 'Components/Field/Field'
import { FieldTypeEnum } from 'Components/Field/Field.types'
import { Form } from 'Components/Form/Form'

import { useActiveUser } from 'Hooks'

import { handleFetchErrorFunctional } from 'Utilities'

import { SentryErrorType } from 'Constants/errors'
import type {
  mutationCreateReturnLabel,
  mutationCreateReturnLabelVariables,
} from 'Constants/graphql/mutations/__generated__/mutationCreateReturnLabel'
import { MUTATION_CREATE_RETURN_LABEL } from 'Constants/graphql/mutations/MutationCreateReturnLabel'

import { useShippingLabelCountryList } from 'Portal/src/hooks/useShippingLabelCountryList/useShippingLabelCountryList'

import { PortalReturnLabelDeviceIdentification } from './PortalReturnLabelDeviceIdentification'
import {
  PORTAL_RETURN_LABEL_ADDRESS_FIELD_MAX_LENGTH,
  PORTAL_RETURN_LABEL_MAX_DEVICES,
  PORTAL_RETURN_LABEL_MODAL_FORM,
} from './PortalReturnLabelModal.constants'
import {
  portalReturnLabelAddressSchema,
  portalReturnLabelModalFormSchema,
} from './PortalReturnLabelModal.schema'
import {
  PortalReturnLabelAddressSourceEnum,
  type PortalReturnLabelModalFormSchema,
  type PortalReturnLabelModalProps,
  type PortalReturnLabelModalSubmitResult,
  type PortalReturnLabelSelectableReturnCase,
} from './PortalReturnLabelModal.types'
import { portalReturnLabelFormToInput } from './PortalReturnLabelModal.utils'
import {
  getPortalReturnLabelCustomAddress,
  getPortalReturnLabelOfficeAddress,
} from './PortalReturnLabelModal.values'
import { PortalReturnLabelModalAddDevice } from './PortalReturnLabelModalAddDevice'
import { PortalReturnLabelModalSuccess } from './PortalReturnLabelModalSuccess'
import { searchPortalReturnLabelCases } from './searchPortalReturnLabelCases'

const addressFieldFeedbackValues = {
  amount: PORTAL_RETURN_LABEL_ADDRESS_FIELD_MAX_LENGTH,
}

const addressFieldInputProps = {
  maxLength: PORTAL_RETURN_LABEL_ADDRESS_FIELD_MAX_LENGTH,
}

export const PortalReturnLabelModal = observer(
  (props: PortalReturnLabelModalProps) => {
    const { onClose, onSubmit, returnCase, senderCountry } = props
    const intl = useIntl()
    const apolloClient = useApolloClient()
    const { activeEmployee, activeOrganisation } = useActiveUser()
    const organisation =
      activeOrganisation || activeEmployee?.organisation || null
    const countryList = useShippingLabelCountryList()
    const [submitError, setSubmitError] = useState<Error | null>(null)
    const [successResult, setSuccessResult] =
      useState<PortalReturnLabelModalSubmitResult | null>(null)
    const [createReturnLabel, { loading, error }] = useMutation<
      mutationCreateReturnLabel,
      mutationCreateReturnLabelVariables
    >(MUTATION_CREATE_RETURN_LABEL)

    const officeAddress = useMemo(
      () =>
        getPortalReturnLabelOfficeAddress(
          returnCase.employee,
          organisation,
          countryList,
        ),
      [countryList, organisation, returnCase.employee],
    )

    const customAddress = useMemo(
      () =>
        getPortalReturnLabelCustomAddress(
          activeEmployee,
          activeOrganisation,
          senderCountry,
        ),
      [activeEmployee, activeOrganisation, senderCountry],
    )

    const isAdmin = !!activeOrganisation
    const defaultAddress = isAdmin ? officeAddress : customAddress

    const selectedAddressSourceRef = useRef(defaultAddress.addressSource)
    const lastReturnCaseIdRef = useRef(returnCase.id)

    const formMethods = useForm<PortalReturnLabelModalFormSchema>({
      defaultValues: defaultAddress,
      resolver: zodResolver(portalReturnLabelModalFormSchema),
    })

    const {
      fields: addedReturnCases,
      append: appendReturnCase,
      remove: removeReturnCase,
    } = useFieldArray<
      PortalReturnLabelModalFormSchema,
      'addedReturnCases',
      'fieldId'
    >({
      control: formMethods.control,
      keyName: 'fieldId',
      name: 'addedReturnCases',
    })

    const addressSource = useWatch({
      control: formMethods.control,
      name: 'addressSource',
    })

    // ModalProvider can reuse this component instance across reopens, so we
    // reset submit/success state on case-change and only react to addressSource
    // toggles once the case is stable. A single effect avoids the race where
    // the case-change reset gets undone by an addressSource read of stale state.
    useEffect(() => {
      if (lastReturnCaseIdRef.current !== returnCase.id) {
        lastReturnCaseIdRef.current = returnCase.id
        selectedAddressSourceRef.current = defaultAddress.addressSource
        setSubmitError(null)
        setSuccessResult(null)
        formMethods.reset(defaultAddress)

        return
      }

      if (
        !addressSource ||
        addressSource === selectedAddressSourceRef.current
      ) {
        return
      }

      const nextAddress =
        addressSource === PortalReturnLabelAddressSourceEnum.OFFICE
          ? officeAddress
          : customAddress

      selectedAddressSourceRef.current = addressSource
      formMethods.reset({
        ...nextAddress,
        addedReturnCases: formMethods.getValues('addedReturnCases'),
      })
    }, [
      addressSource,
      customAddress,
      defaultAddress,
      formMethods,
      officeAddress,
      returnCase.id,
    ])

    const isOfficeAddressSelected =
      addressSource === PortalReturnLabelAddressSourceEnum.OFFICE

    const isOfficeAddressIncomplete = useMemo(
      () => !portalReturnLabelAddressSchema.safeParse(officeAddress).success,
      [officeAddress],
    )

    const employeeIdRaw = returnCase.employee?.id ?? activeEmployee?.id ?? null
    const employeeId = isNil(employeeIdRaw) ? null : String(employeeIdRaw)
    const organisationId = isNil(organisation?.id)
      ? null
      : String(organisation?.id)
    const isSubmitDisabled = !employeeId || !organisationId

    // searchReturnCases scopes by employeeID; per BE spec it must be null for
    // admin context so the BE can do email/IMEI/serial lookup across the org.
    const searchEmployeeId = useMemo(
      () =>
        activeOrganisation || isNil(activeEmployee?.id)
          ? null
          : String(activeEmployee.id),
      [activeEmployee?.id, activeOrganisation],
    )

    const selectedReturnCaseIds = useMemo(
      () => [
        String(returnCase.id),
        ...addedReturnCases.map(field => String(field.id)),
      ],
      [addedReturnCases, returnCase.id],
    )

    const handleSearchDevices = useCallback(
      (term: string) =>
        searchPortalReturnLabelCases(apolloClient, {
          employeeId: searchEmployeeId,
          organisationId,
          term,
        }),
      [apolloClient, searchEmployeeId, organisationId],
    )

    const handleAddDevice = useCallback(
      (rc: PortalReturnLabelSelectableReturnCase) => {
        appendReturnCase(rc)
      },
      [appendReturnCase],
    )

    const handleRemoveDevice = useCallback(
      (index: number) => {
        removeReturnCase(index)
      },
      [removeReturnCase],
    )

    const handleSubmit = formMethods.handleSubmit(async data => {
      setSubmitError(null)

      if (!employeeId || !organisationId) {
        setSubmitError(new Error(intl.formatMessage({ id: 'Error_occurred' })))

        return
      }

      const mutationInput = portalReturnLabelFormToInput(data, {
        employeeId,
        organisationId,
        returnCaseIds: selectedReturnCaseIds,
      })

      let mutationResult

      try {
        mutationResult = await createReturnLabel({
          variables: {
            input: mutationInput,
          },
        })
      } catch (caughtError) {
        setSubmitError(
          caughtError instanceof Error
            ? caughtError
            : new Error(intl.formatMessage({ id: 'Error_occurred' })),
        )

        return
      }

      const createdReturnLabel = mutationResult.data?.createReturnLabel

      if (!createdReturnLabel) {
        setSubmitError(new Error(intl.formatMessage({ id: 'Error_occurred' })))

        return
      }

      // Mutation succeeded - the label exists on BE. Refetch is best-effort
      // and must not invalidate the success state, otherwise the user could
      // retry an already-successful submission.
      let refetchedResult: PortalReturnLabelModalSubmitResult | null = null

      try {
        refetchedResult = await onSubmit()
      } catch (refetchError) {
        handleFetchErrorFunctional()(
          refetchError instanceof Error
            ? refetchError
            : new Error('PortalReturnLabelModal refetch failed'),
          {
            operation: {
              operationName: 'PortalReturnLabelModal_refetch_after_create',
            },
            trackingReference: createdReturnLabel.trackingReference,
            type: SentryErrorType.GQL_ERROR,
          },
        )
      }

      // Refetch may return stale data from a previous label if the query
      // hasn't caught up yet (e.g. regenerating a label in the same session).
      // Detect this by comparing tracking references: if they differ the
      // refetch has the wrong label's data, so treat it the same as a failed
      // refetch and fall back fully to the authoritative mutation result.
      const refetchDataIsStale =
        !!refetchedResult?.trackingReference &&
        refetchedResult.trackingReference !== createdReturnLabel.trackingReference

      const safeRefetch = refetchDataIsStale ? null : refetchedResult

      setSuccessResult({
        dangerousGoodsPdf:
          safeRefetch?.dangerousGoodsPdf ?? createdReturnLabel.dangerousGoodsPdf,
        isDangerousGoods:
          safeRefetch?.isDangerousGoods ?? createdReturnLabel.isDangerousGoods,
        organisationId: safeRefetch?.organisationId ?? organisationId,
        pdfDownloadUrl: safeRefetch?.pdfDownloadUrl ?? null,
        trackingReference: createdReturnLabel.trackingReference,
      })
    })

    return (
      <Card className="PortalReturnLabelModal-card">
        <CardHeader icon={faTruckArrowRight}>
          <FormattedMessage id="PublicRequestReturnLabel_title" />
        </CardHeader>

        <CardBody className="PortalReturnLabelModal-body">
          {successResult ? (
            <PortalReturnLabelModalSuccess
              onClose={onClose}
              result={successResult}
            />
          ) : (
            <>
              <FormProvider {...formMethods}>
                <Form
                  aria-busy={loading || formMethods.formState.isSubmitting}
                  id={PORTAL_RETURN_LABEL_MODAL_FORM}
                  onSubmit={handleSubmit}
                >
                  <fieldset
                    className="PortalReturnLabelModal-fieldset"
                    disabled={loading || formMethods.formState.isSubmitting}
                  >
                    <Heading as={6} className="mb-4" size={6}>
                      <FormattedMessage id="PortalReturnLabelModal_device_identification" />
                    </Heading>

                    <PortalReturnLabelDeviceIdentification
                      returnCase={returnCase}
                    />

                    {addedReturnCases.map((field, index) => (
                      <PortalReturnLabelDeviceIdentification
                        key={field.fieldId}
                        onRemove={() => handleRemoveDevice(index)}
                        returnCase={field}
                      />
                    ))}

                    <PortalReturnLabelModalAddDevice
                      excludedReturnCaseIds={selectedReturnCaseIds}
                      isAdmin={isAdmin}
                      isAtLimit={
                        selectedReturnCaseIds.length >=
                        PORTAL_RETURN_LABEL_MAX_DEVICES
                      }
                      onAdd={handleAddDevice}
                      search={handleSearchDevices}
                    />

                    <Heading as={6} className="mb-4" size={6}>
                      <FormattedMessage id="PublicRequestReturnLabel_sender_heading" />
                    </Heading>

                    <div className="d-flex flex-column flex-md-row gap-4 mb-5">
                      <Field
                        label="Custom_address"
                        name="addressSource"
                        type={FieldTypeEnum.radio}
                        value={PortalReturnLabelAddressSourceEnum.CUSTOM}
                      />
                      <Field
                        label="Office"
                        name="addressSource"
                        type={FieldTypeEnum.radio}
                        value={PortalReturnLabelAddressSourceEnum.OFFICE}
                      />
                    </div>

                    {isOfficeAddressSelected && isOfficeAddressIncomplete && (
                      <Alert
                        className="mb-4"
                        iconLeft={faCircleExclamation}
                        variant="warning"
                      >
                        <FormattedMessage id="PortalReturnLabelModal_office_address_incomplete" />
                      </Alert>
                    )}

                    <div className="mb-6">
                      <Field
                        autoComplete="name"
                        disabled={isOfficeAddressSelected}
                        feedbackTranslationValues={addressFieldFeedbackValues}
                        inputProps={addressFieldInputProps}
                        label="PublicRequestReturnLabel_sender_fullName"
                        markAsRequired
                        name="fullName"
                        type={FieldTypeEnum.text}
                      />
                      <Row className="mt-4">
                        <Col md={8} xs={12}>
                          <Field
                            autoComplete="address-line1"
                            disabled={isOfficeAddressSelected}
                            feedbackTranslationValues={
                              addressFieldFeedbackValues
                            }
                            inputProps={addressFieldInputProps}
                            label="PublicRequestReturnLabel_sender_street"
                            markAsRequired
                            name="street"
                            type={FieldTypeEnum.text}
                          />
                        </Col>
                        <Col className="mt-3 mt-md-0" md={4} xs={12}>
                          <Field
                            autoComplete="off"
                            disabled={isOfficeAddressSelected}
                            feedbackTranslationValues={
                              addressFieldFeedbackValues
                            }
                            inputProps={addressFieldInputProps}
                            label="PublicRequestReturnLabel_sender_streetNumber"
                            markAsRequired
                            name="streetNumber"
                            type={FieldTypeEnum.text}
                          />
                        </Col>
                      </Row>

                      <div className="d-flex flex-column flex-md-row gap-3 mt-4">
                        <Field
                          autoComplete="postal-code"
                          containerClassName="flex-grow-1"
                          disabled={isOfficeAddressSelected}
                          feedbackTranslationValues={addressFieldFeedbackValues}
                          inputProps={addressFieldInputProps}
                          label="PublicRequestReturnLabel_sender_zipCode"
                          markAsRequired
                          name="zipcode"
                          type={FieldTypeEnum.text}
                        />
                        <Field
                          autoComplete="address-level2"
                          containerClassName="flex-grow-1"
                          disabled={isOfficeAddressSelected}
                          feedbackTranslationValues={addressFieldFeedbackValues}
                          inputProps={addressFieldInputProps}
                          label="PublicRequestReturnLabel_sender_city"
                          markAsRequired
                          name="city"
                          type={FieldTypeEnum.text}
                        />
                      </div>

                      <CountrySelect
                        containerClassName="mt-4"
                        disabled
                        isShippingLabelList
                        label="PublicRequestReturnLabel_sender_country"
                        markAsRequired
                        name="country"
                      />
                    </div>
                  </fieldset>
                </Form>
              </FormProvider>

              {(error || submitError) && (
                <div className="mb-4">
                  <AlertWithError error={error || submitError} />
                </div>
              )}

              <p className="mb-4 text-muted">
                <FormattedMessage id="PortalReturnLabelModal_submit_notice" />
              </p>

              <ButtonGroup
                leftButton={{
                  disabled: loading || formMethods.formState.isSubmitting,
                  onClick: onClose,
                  text: intl.formatMessage({ id: 'Cancel' }),
                }}
                rightButton={{
                  disabled: isSubmitDisabled,
                  form: PORTAL_RETURN_LABEL_MODAL_FORM,
                  iconLeft: faTruckArrowRight,
                  isLoading: loading || formMethods.formState.isSubmitting,
                  text: intl.formatMessage({
                    id: 'PublicRequestReturnLabel_submit',
                  }),
                  type: 'submit',
                }}
              />
            </>
          )}
        </CardBody>
      </Card>
    )
  },
)
